<template>
    <MainLayout selectMenu="23-1"  >
    <!-- Upload area (always visible) -->
    <div class="curve-view-toolbar">
        <Upload :action="importUrl" ref="curveViewFileUpload" name="file"
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            :show-upload-list="false" :on-success="successImport" :on-error="errorImport"
            :before-upload="beforeUpload">
            <Button icon="ios-cloud-upload-outline" type="info" :loading="uploading">{{ getTranslation('k114') }}</Button>
        </Upload>
        <template v-if="records.length > 0">
            <Input v-model="searchRecordId" :placeholder="getTranslation('r105')" clearable
                style="width: 220px" @on-change="onSearchChange" />
      
            <span class="record-indicator">
                {{ getTranslation('c010') }} {{ filteredRecords.length }} {{ getTranslation('c011') }}
            </span>
            <Page :total="filteredRecords.length" :current="currentPage" :page-size="pageSize"
                size="small" show-total @on-change="handlePageChange"
                :show-sizer="false" />
        </template>
    </div>

    <!-- Loading state -->
    <div v-if="uploading || chartLoading" class="loading-wrapper">
        <LoadingBar :height="'400px'" />
    </div>

    <!-- Empty state -->
    <div v-else-if="records.length === 0" class="empty-state">
        <Icon type="ios-cloud-upload-outline" size="64" color="#c5c8ce" />
        <p>{{ getTranslation('e059') }}</p>
    </div>

    <!-- Record display -->
    <div v-else class="record-display-container">
        <!-- Record header bar -->
        <div :class="['record-header', { 'record-header-alarm': currentRecord.AlarmCnt > 0 }]">
            <div class="header-item">
                <span class="header-label">{{ getTranslation('k222') }}</span>
                <span class="header-value header-code">{{ formatShorTime(currentRecord.created_string) }}</span>
            </div>
            <span class="header-divider">|</span>
            <div class="header-item">
                <span class="header-label">{{ getTranslation('r105') }}</span>
                <span class="header-value">{{ currentRecord.RecordId }}</span>
            </div>          
            <span class="header-divider">|</span>
            <div class="header-item">
                <span class="header-label">{{ getTranslation('k259') }}</span>
                <span class="header-value">{{ currentRecord.SpotId }}</span>
            </div>
            <span class="header-divider">|</span>
            <div class="header-item">
                <span class="header-label">{{ getTranslation('b006') }}</span>
                <span class="header-value">{{ currentRecord.NoWeldProgram }}</span>
            </div>
            <span class="header-divider">|</span>
            <div class="header-item">
                <span class="header-label">{{ getTranslation('k230') }}</span>
                <span class="header-value">{{ currentRecord.ValCurrentGun }}</span>
            </div>
            <span class="header-divider">|</span>
            <div class="header-item">
                <span class="header-label">{{ getTranslation('l005') }}</span>
                <span class="header-value" :class="{ 'header-alarm-value': currentRecord.AlarmCnt > 0 }">{{ currentRecord.AlarmCnt }}</span>
            </div>
        </div>

        <!-- Scrollable content area -->
        <div class="record-scroll-content">
            <!-- Basic info section -->
            <Card class="card-container" dis-hover>
                <div slot="title" class="card-title">{{ getTranslation('r101') }}</div>
                <div class="info-grid">
                    <Row type="flex" class="normal-row table-row-odd">
                        <Col flex="1">{{ getTranslation('b006') }}:</Col>
                        <Col flex="2">{{ currentRecord.NoWeldProgram }}</Col>
                        <Col flex="1">{{ getTranslation('b007') }}:</Col>
                        <Col flex="2">{{ currentRecord.WeldProgram }}</Col>
                        <Col flex="1">{{ getTranslation('k269') }}:</Col>
                        <Col flex="2">{{ currentRecord.PartName }}</Col>
                        <Col flex="1">{{ getTranslation('k172') }}:</Col>
                        <Col flex="2">{{ currentRecord.BatchNumber }}</Col>
                        <Col flex="1">{{ getTranslation('u008') }}:</Col>
                        <Col flex="2">{{ currentRecord.Worker }}</Col>
                        <Col flex="1">{{ getTranslation('k258') }}:</Col>
                        <Col flex="2">{{ currentRecord.SpotModel }}</Col>
                    </Row>
                    <Row type="flex" class="normal-row">
                        <Col flex="1">{{ getTranslation('k241') }}:</Col>
                        <Col flex="2">{{ currentRecord.SpotName }}</Col>
                        <Col flex="1">{{ getTranslation('k243') }}:</Col>
                        <Col flex="2">{{ currentRecord.SpotPss }}</Col>
                        <Col flex="1">{{ getTranslation('r014') }}:</Col>
                        <Col flex="2"><template v-if="currentRecord.ValCurrentGun == 1">{{ currentRecord.ValGun1SingleProdSpotCnt }}</template>
                            <template v-else-if="currentRecord.ValCurrentGun == 2">{{ currentRecord.ValGun2SingleProdSpotCnt }}</template>
                            <template v-else>-</template></Col>
                        <Col flex="1">{{ getTranslation('r015') }}:</Col>
                        <Col flex="2"><template v-if="currentRecord.ValCurrentGun == 1">{{ currentRecord.ValGun1ScheduleProdMfgCnt }}</template>
                            <template v-else-if="currentRecord.ValCurrentGun == 2">{{ currentRecord.ValGun2ScheduleProdMfgCnt }}</template>
                            <template v-else>-</template></Col>
                        <Col flex="1">{{ getTranslation('r016') }}:</Col>
                        <Col flex="2"><template v-if="currentRecord.ValCurrentGun == 1">{{ currentRecord.ValGun1TotalProdMfgCnt }}</template>
                            <template v-else-if="currentRecord.ValCurrentGun == 2">{{ currentRecord.ValGun2TotalProdMfgCnt }}</template>
                            <template v-else>-</template></Col>
                        <Col flex="1">{{ getTranslation('r017') }}:</Col>
                        <Col flex="2"><template v-if="currentRecord.ValCurrentGun == 1">{{ currentRecord.ValGun1TotalProdSpotCnt }}</template>
                            <template v-else-if="currentRecord.ValCurrentGun == 2">{{ currentRecord.ValGun2TotalProdSpotCnt }}</template>
                            <template v-else-if="currentRecord.ValCurrentGun == 3">{{ currentRecord.ValGun3TotalProdSpotCnt }}</template>
                            <template v-else-if="currentRecord.ValCurrentGun == 4">{{ currentRecord.ValGun4TotalProdSpotCnt }}</template>
                            <template v-else-if="currentRecord.ValCurrentGun == 5">{{ currentRecord.ValGun5TotalProdSpotCnt }}</template>
                            <template v-else>-</template></Col>
                    </Row>
                    <Row type="flex" class="normal-row table-row-odd">
                        <Col flex="1">{{ getTranslation('r018') }}:</Col>
                        <Col flex="2"><template v-if="currentRecord.ValCurrentGun == 1">{{ currentRecord.ValGun1StepIncNumCnt }}</template>
                            <template v-else-if="currentRecord.ValCurrentGun == 2">{{ currentRecord.ValGun2StepIncNumCnt }}</template>
                            <template v-else-if="currentRecord.ValCurrentGun == 3">{{ currentRecord.ValGun3StepIncNumCnt }}</template>
                            <template v-else-if="currentRecord.ValCurrentGun == 4">{{ currentRecord.ValGun4StepIncNumCnt }}</template>
                            <template v-else-if="currentRecord.ValCurrentGun == 5">{{ currentRecord.ValGun5StepIncNumCnt }}</template>
                            <template v-else>-</template></Col>
                        <Col flex="1">{{ getTranslation('r019') }}:</Col>
                        <Col flex="2"><template v-if="currentRecord.ValCurrentGun == 1">{{ currentRecord.ValGun1DressedNumCnt }}</template>
                            <template v-else-if="currentRecord.ValCurrentGun == 2">{{ currentRecord.ValGun2DressedNumCnt }}</template>
                            <template v-else-if="currentRecord.ValCurrentGun == 3">{{ currentRecord.ValGun3DressedNumCnt }}</template>
                            <template v-else-if="currentRecord.ValCurrentGun == 4">{{ currentRecord.ValGun4DressedNumCnt }}</template>
                            <template v-else-if="currentRecord.ValCurrentGun == 5">{{ currentRecord.ValGun5DressedNumCnt }}</template>
                            <template v-else>-</template></Col>
                        <Col flex="1">{{ getTranslation('r020') }}:</Col>
                        <Col flex="2">{{ currentRecord.ValPrimCapVolt }}</Col>
                        <Col flex="1">{{ getTranslation('k226') }}:</Col>
                        <Col flex="2">{{ currentRecord.Vin }}</Col>
                        <Col flex="1">{{ getTranslation('k231') }}:</Col>
                        <Col flex="2">{{ currentRecord.ValPowerCnt }}</Col>
                        <Col flex="1">{{ getTranslation('k232') }}:</Col>
                        <Col flex="2">{{ currentRecord.ValDutyOnTime }}</Col>
                    </Row>
                    <Row type="flex" class="normal-row">
                        <Col flex="1">{{ getTranslation('r021') }}:</Col>
                        <Col flex="2">{{ currentRecord.ValPrgExpulsionCnt }}</Col>
                        <Col flex="1">{{ getTranslation('r022') }}:</Col>
                        <Col flex="2">{{ currentRecord.ValSysExpulsionCnt }}</Col>
                        <Col flex="1">{{ getTranslation('k233') }}:</Col>
                        <Col flex="2">{{ currentRecord.ValSecVolt }}</Col>
                        <Col flex="1">{{ getTranslation('k234') }}:</Col>
                        <Col flex="2">{{ currentRecord.ValSecRest }}</Col>
                        <Col flex="1">{{ getTranslation('k235') }}:</Col>
                        <Col flex="2">{{ currentRecord.DispRsv10 }}</Col>
                        <Col flex="3"></Col>
                    </Row>                   
                </div>
                <div class="other-line">
                    <Row type="flex" class="row-line table-row-odd">
                        <Col flex="1"></Col>
                        <Col flex="2" class="flex-middle">{{ getTranslation('b018') }}</Col>
                        <Col flex="2" class="flex-middle">{{ getTranslation('b019') }}</Col>
                        <Col flex="2" class="flex-middle">{{ getTranslation('b020') }}</Col>
                        <Col flex="2" class="flex-middle">{{ getTranslation('b021') }}</Col>
                        <Col flex="2" class="flex-middle">{{ getTranslation('b022') }}</Col>
                        <Col flex="2" class="flex-middle">{{ getTranslation('b023') }}</Col>
                        <Col flex="2" class="flex-middle">{{ getTranslation('b024') }}</Col>
                        <Col flex="2" class="flex-middle">{{ getTranslation('b025') }}</Col>
                        <Col flex="2" class="flex-middle">{{ getTranslation('b095') }}</Col>                        
                        <Col flex="2" class="flex-middle">{{ getTranslation('b026') }}</Col>
                        <Col flex="2" class="flex-middle">{{ getTranslation('b027') }}</Col>
                        <Col flex="2" class="flex-middle">{{ getTranslation('b028') }}</Col>
                        <Col flex="2" class="flex-middle">{{ getTranslation('b029') }}</Col>
                        <Col flex="2" class="flex-middle">{{ getTranslation('b030') }}</Col>
                        <Col flex="2" class="flex-middle">{{ getTranslation('b031') }}</Col>
                        <Col flex="2" class="flex-middle">{{ getTranslation('b032') }}</Col>
                        <Col flex="1"></Col>
                    </Row>
                    <Row type="flex" class="row-line">
                        <Col flex="1"><img class="other-img" :src="require('@/assets/img/weld-crr.png')" /></Col>
                        <Col flex="4"></Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.CrrPreWeld }}</Col>
                        <Col flex="4"></Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.CrrWeld1 }}</Col>
                        <Col flex="2"></Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.CrrWeld2 }}</Col>
                        <Col flex="2"></Col>
                        <Col flex="2"></Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.CrrWeld3 }}</Col>
                        <Col flex="4"></Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.CrrPostWeld }}</Col>
                        <Col flex="4"></Col>
                        <Col flex="1" class="flex-middle">KA</Col>
                    </Row>
                    <Row type="flex" class="row-line table-row-odd">
                        <Col flex="1"><img class="other-img" :src="require('@/assets/img/weld-time.png')" /></Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.TimPrePress }}</Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.TimMorePress }}</Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.TimPreWeld }}</Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.TimCold0 }}</Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.TimAscentWeld }}</Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.TimWeld1 }}</Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.TimCold1 }}</Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.TimWeld2 }}</Col>                        
                        <Col flex="2"></Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.TimCold2 }}</Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.TimWeld3 }}</Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.TimDescendWeld }}</Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.TimCold3 }}</Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.TimPostWeld }}</Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.TimKeep }}</Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.TimRelease }}</Col>
                        <Col flex="1" class="flex-middle">ms</Col>
                    </Row>
                    <Row type="flex" class="row-line table-row-even">
                        <Col flex="17"></Col>
                        <Col flex="2" class="flex-middle">{{ currentRecord.NumCurrSpot }}</Col>
                        <Col flex="15"></Col>
                    </Row>
                    <Row type="flex" class="row-line table-row-odd">
                            <Col flex="3">{{ getTranslation('k218') }}</Col>
                            <Col flex="2" class="flex-middle"></Col>
                            <Col flex="2" class="flex-middle">{{currentRecord.ProgramCrrPreWeld}}</Col>
                            <Col flex="2" class="flex-middle"></Col>
                            <Col flex="2" class="flex-middle"></Col>
                            <Col flex="2" class="flex-middle">{{currentRecord.ProgramCrrWeld1}}</Col>
                            <Col flex="2" class="flex-middle"></Col>
                            <Col flex="2" class="flex-middle">{{currentRecord.ProgramCrrWeld2}}</Col>
                            <Col flex="2" class="flex-middle">{{currentRecord.ProgramNumWeldPulse2}}</Col>                            
                            <Col flex="2" class="flex-middle"></Col>
                            <Col flex="2" class="flex-middle">{{currentRecord.ProgramCrrWeld3}}</Col>
                            <Col flex="2" class="flex-middle"></Col>
                            <Col flex="2" class="flex-middle"></Col>
                            <Col flex="2" class="flex-middle">{{currentRecord.ProgramCrrPostWeld}}</Col>
                            <Col flex="2" class="flex-middle"></Col>
                            <Col flex="2" class="flex-middle"></Col>
                            <Col flex="1" class="flex-middle">KA</Col>
                        </Row>
                </div>
            </Card>
            

            <!-- Curve chart (always rendered when records exist, v-show for ref stability) -->
            <div v-show="hasCurveData" class="chart-container">
                <div ref="curveChart" :style="{ width: chartWidth, height: '500px' }"></div>
            </div>
            <div v-show="!hasCurveData" class="no-curve-hint">
                {{ getTranslation('e060') }}
            </div>
        </div>

        <!-- Navigation buttons overlay -->
        <div class="nav-button nav-prev" @click="navChange(-1)" :class="{ disabled: !canPrev }">
            <Icon type="ios-arrow-back" />
        </div>
        <div class="nav-button nav-next" @click="navChange(1)" :class="{ disabled: !canNext }">
            <Icon type="ios-arrow-forward" />
        </div>
    </div>
   
  </MainLayout>
</template>

<script>
import MainLayout from '@/components/MainLayout.vue'

import { mapGetters } from 'vuex';
import { API_URL } from '@/constants/const';
import { formatShorTime } from '@/util/util.js';
import LoadingBar from '@/components/LoadingBar.vue';

export default {
    name: 'BrowserIndex',
    components: {
         LoadingBar,  MainLayout
    },
    props: {
        propVisible: Boolean,
    },
    data() {
        return {
            modalLoading: true,
            uploading: false,
            chartLoading: false,
            records: [],
            searchRecordId: '',          
            currentIndex: 0,
            currentPage: 1,
            pageSize: 1,
            myChart: null,
            chartWidth: '100%',
            yAxisDefine: [],
        }
    },
    computed: {
        ...mapGetters(['getTranslation']),
        visible: {
            get() { return this.propVisible },
            set(val) { this.$emit('input', val) }
        },
        importUrl() {
            return API_URL['recordCurveView']
        },
        filteredRecords() {
            let list = this.records;
            const rid = this.searchRecordId.trim();           
            if (rid) {
                list = list.filter(r => {
                    const rec = r.record || {};
                    return (rec.RecordId || '').indexOf(rid) !== -1;
                });
            }           
            return list;
        },
        filteredIndex() {
            // currentIndex in filteredRecords space
            return (this.currentPage - 1) * this.pageSize;
        },
        modalTitle() {
            const total = this.records.length;
            const filtered = this.filteredRecords.length;
            if (total > 0) {
                if (filtered !== total) {
                    return this.getTranslation('e042') + ' (' + filtered + '/' + total + ')';
                }
                return this.getTranslation('e042') + ' (' + total + ')';
            }
            return this.getTranslation('e042');
        },
        currentRecord() {
            const recs = this.filteredRecords;
            const idx = this.filteredIndex;
            if (recs.length > 0 && idx < recs.length) {
                return recs[idx].record || {};
            }
            return {};
        },
        currentCurves() {
            const recs = this.filteredRecords;
            const idx = this.filteredIndex;
            if (recs.length > 0 && idx < recs.length) {
                return recs[idx].curves || {};
            }
            return {};
        },
        currentXAxis() {
            const recs = this.filteredRecords;
            const idx = this.filteredIndex;
            if (recs.length > 0 && idx < recs.length) {
                return recs[idx].xAxis || [];
            }
            return [];
        },
        hasCurveData() {
            const c = this.currentCurves;
            return c && (c.current || c.voltage || c.resistance || c.press);
        },
        canPrev() {
            return this.currentPage > 1;
        },
        canNext() {
            return this.currentPage * this.pageSize < this.filteredRecords.length;
        },
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                this.modalLoading = false;
                // Reset on close-reopen
                if (this.records.length === 0) {
                    this.currentIndex = 0;
                }
            }
        },
        currentPage() {
            this.$nextTick(() => {
                if (this.$refs.curveChart) {
                    this.renderCurves();
                }
            });
        },
        searchRecordId() {
            this.$nextTick(() => {
                if (this.$refs.curveChart) {
                    this.renderCurves();
                }
            });
        },
       
    },
    methods: {
        formatShorTime(str) {
            return formatShorTime(str);
        },
        cancel() {
            this.visible = false;
        },
        beforeUpload() {
            this.uploading = true;
            this.records = [];
            this.searchRecordId = '';            
            this.currentPage = 1;
            if (this.myChart) {
                this.myChart.dispose();
                this.myChart = null;
            }
            return true;
        },
        errorImport() {
            this.uploading = false;
            this.$Message.error(this.getTranslation('c001'));
        },
        successImport(response) {
            this.uploading = false;
            if (response && response.code == 200) {
                const data = response.data;
                if (data && data.records && data.records.length > 0) {
                    this.records = data.records;
                    this.searchRecordId = '';                    
                    this.currentPage = 1;
                    this.$nextTick(() => {
                        this.renderCurves();
                    });                    
                } else {
                    this.$Message.warning(this.getTranslation('b187'));
                }
            } else if (response && response.msg) {
                this.$Message.error(response.msg);
            } else {
                this.$Message.error(this.getTranslation('c001'));
            }
        },
        navChange(delta) {
            const newPage = this.currentPage + delta;
            const maxPage = Math.ceil(this.filteredRecords.length / this.pageSize);
            if (newPage >= 1 && newPage <= maxPage) {
                this.currentPage = newPage;
            }
        },
        handlePageChange(page) {
            this.currentPage = page;
        },
        onSearchChange() {
            this.currentPage = 1;
        },
        initChart() {
            if (this.myChart) {
                this.myChart.dispose();
                this.myChart = null;
            }
            if (!this.$refs.curveChart) return;
            const parentWidth = this.$refs.curveChart.offsetWidth;
            this.chartWidth = parentWidth + 'px';
            this.myChart = this.$echarts.init(this.$refs.curveChart);
            this.yAxisDefine = [
                {
                    name: this.getTranslation('r081'),
                    show: true, offset: 0, position: 'left',
                    nameLocation: 'middle', axisLine: { onZero: true },
                    type: 'value',
                    nameTextStyle: { color: '#f00' },
                    axisLabel: { color: '#f00' }
                },
                {
                    name: this.getTranslation('r082'),
                    show: true, offset: 0, position: 'right',
                    nameLocation: 'middle', axisLine: { onZero: true },
                    type: 'value',
                    nameTextStyle: { color: '#00f' },
                    axisLabel: { color: '#00f' }
                },
                {
                    name: this.getTranslation('r083'),
                    show: true, offset: 20, position: 'left',
                    nameLocation: 'middle', axisLine: { onZero: true },
                    type: 'value',
                    nameTextStyle: { color: '#0f0' },
                    axisLabel: { color: '#0f0' }
                },
                {
                    name: this.getTranslation('r084'),
                    show: true, offset: 20, position: 'right',
                    nameLocation: 'middle', axisLine: { onZero: true },
                    type: 'value',
                    nameTextStyle: { color: '#FFA500' },
                    axisLabel: { color: '#FFA500' }
                }
            ];
            this.myChart.setOption({
                title: { text: 'weld-curve', show: false },
                toolbox: {
                    feature: {
                        saveAsImage: {
                            title : this.getTranslation('e066') ,
                        },
                    }
                },
                tooltip: { trigger: 'axis' },
                grid: { left: '35px', right: '45px', top: '40px' },
                yAxis: this.yAxisDefine,
                dataZoom: [{
                    type: 'slider', xAxisIndex: 0,
                    bottom: 30, filterMode: 'empty',
                    showDataShadow: true
                }, {
                    type: 'slider', yAxisIndex: [0, 1, 2, 3],
                    filterMode: 'empty', right: 0,
                    showDataShadow: true
                }],
                xAxis: { data: [] },
                legend: { data: [], bottom: 0 },
                series: [
                    { name: this.getTranslation('r081'), type: 'line', data: [] },
                    { name: this.getTranslation('r082'), type: 'line', data: [] },
                    { name: this.getTranslation('r083'), type: 'line', data: [] },
                    { name: this.getTranslation('r084'), type: 'line', data: [] },
                ]
            });
        },
        renderCurves() {
            const curves = this.currentCurves;
            const xAxis = this.currentXAxis;
            if (!curves || Object.keys(curves).length === 0) {
                return;
            }
            if (!this.myChart) {
                this.initChart();
            }
            if (!this.myChart) return;

            let aSeries = [];
            let aLegend = [];

            const curveDefs = [
                { key: 'current', name: this.getTranslation('r081'), color: '#f00', yIdx: 0 },
                { key: 'voltage', name: this.getTranslation('r082'), color: '#00f', yIdx: 1 },
                { key: 'resistance', name: this.getTranslation('r083'), color: '#0f0', yIdx: 2 },
                { key: 'press', name: this.getTranslation('r084'), color: '#FFA500', yIdx: 3 },
            ];

            curveDefs.forEach(def => {
                const data = curves[def.key];
                if (data && data.length > 0) {
                    aLegend.push(def.name);
                    aSeries.push({
                        name: def.name,
                        type: 'line',
                        showSymbol: false,
                        color: def.color,
                        lineStyle: { width: 1 },
                        yAxisIndex: def.yIdx,
                        data: data
                    });
                    this.yAxisDefine[def.yIdx].show = true;
                } else {
                    this.yAxisDefine[def.yIdx].show = false;
                    aSeries.push({
                        name: def.name, type: 'line', data: []
                    });
                }
            });

            this.myChart.setOption({
                legend: { data: aLegend, bottom: 0 },
                yAxis: this.yAxisDefine,
                xAxis: {
                    axisLine: { onZero: true },
                    type: 'category',
                    boundaryGap: false,
                    splitLine: { show: true },
                    data: xAxis
                },
                series: aSeries
            });
        },
    },
    beforeDestroy() {
        if (this.myChart) {
            this.myChart.dispose();
            this.myChart = null;
        }
    },
}
</script>

<style scoped>
@import url('../../assets/style/list.css');
@import url('../../assets/style/tab.css');
.curve-view-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
    margin-bottom: 8px;
    border-bottom: 1px solid #e8eaec;
}

.record-indicator {
    font-size: 14px;
    font-weight: 600;
    color: #515a6e;
    background: #f0f2f5;
    padding: 4px 12px;
    border-radius: 4px;
}

.loading-wrapper {
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.empty-state {
    min-height: 300px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #c5c8ce;
}

.empty-state p {
    margin-top: 16px;
    font-size: 14px;
}

.record-display-container {
    position: relative;
}

.record-scroll-content {
    max-height: calc(100vh - 260px);
    overflow-y: auto;
    padding-right: 4px;
}

/* Record header bar */
.record-header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    column-gap: 12px;
    row-gap: 2px;
    padding: 4px 8px;
    margin-bottom: 8px;
    background: linear-gradient(180deg, #f8f9fa 0%, #eef0f2 100%);
    border-bottom: 2px solid #d0d5dd;
    border-radius: 4px 4px 0 0;
    font-size: 13px;
}

.record-header-alarm {
    background: linear-gradient(180deg, #fff5f5 0%, #ffe0e0 100%);
    border-bottom-color: #ff4d4f;
}

.header-item {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    white-space: nowrap;
}

.header-label {
    color: #8c8c8c;
    font-size: 12px;
    font-weight: 400;
}

.header-label::after {
    content: ':';
}

.header-value {
    color: #262626;
    font-size: 13px;
    font-weight: 600;
}

.header-code {
    color: #1890ff;
    font-size: 14px;
    font-weight: 700;
}

.header-alarm-value {
    color: #ff4d4f;
    font-weight: 700;
}

.header-divider {
    color: #d9d9d9;
    font-size: 14px;
    user-select: none;
}

/* Cards */
.card-container {
    margin-bottom: 8px;
}

.card-title {
    font-weight: 600;
    font-size: 14px;
}

.info-grid {
    padding: 4px 0;
}

.normal-row {
    margin-bottom: 5px;
}

.other-img {
    width: 14px;
}

.row-line {
    margin: 5px 0;
}

/* Table styles */
.table {
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    overflow: hidden;
}

.table .ivu-row {
    border-bottom: 1px solid #e8e8e8;
}

.table .ivu-col {
    border-right: 1px solid #e8e8e8;
    padding: 8px 4px;
    min-height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.table .ivu-row:first-child .ivu-col {
    background-color: #f5f5f5;
    font-weight: bold;
}

/* Curve chart */
.chart-container {
    margin-top: 8px;
    border: 1px solid #e8eaec;
    border-radius: 4px;
    padding: 8px;
}

.no-curve-hint {
    text-align: center;
    padding: 40px;
    color: #999;
    font-size: 14px;
}

/* Navigation buttons */
.nav-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.3s ease;
    color: white;
    font-size: 20px;
    z-index: 10;
}

.nav-button:hover:not(.disabled) {
    background-color: rgba(0, 0, 0, 0.5);
    transform: translateY(-50%) scale(1.1);
}

.nav-button.disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.nav-prev {
    left: -10px;
}

.nav-next {
    right: -10px;
}

::v-deep(.ivu-input.ivu-input-disabled),
::v-deep(.ivu-input[disabled]) {
    color: #999 !important;
    background-color: #f8f9fa !important;
    border-color: #ced4da !important;
    opacity: 1 !important;
    -webkit-text-fill-color: #999 !important;
}
</style>
